import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { FMGModal } from 'components/common/fmgModal';
import { Button, Col, Row, Select, Typography, Badge } from 'components';
import { SupplierSelect } from 'components/common/supplierSelect';
import { usePurchaseOrderItemHistoricalDataQuery } from 'api';
import { useProcessedChartData, useChartConfiguration, useChartNavigation, DATE_RANGE_TYPES } from './hooks';
import { HistoricalChart, HistoricalDataTable } from './components';
import { screamingSnakeToTitleCase } from '../../../../../../../utils/helpers';

const { Text } = Typography;
const { Option } = Select;

interface IHistoricalDataModal {
  open: boolean;
  onClose: () => void;
  itemSku?: string;
  initialSupplierId?: number;
}

const DATE_RANGE_OPTIONS = [
  { label: 'Last 1 Month', value: DATE_RANGE_TYPES.last_month },
  { label: 'Last 6 Months', value: DATE_RANGE_TYPES.last_6_month },
  { label: 'Last Year', value: DATE_RANGE_TYPES.last_year },
];

export const HistoricalDataModal: FC<IHistoricalDataModal> = React.memo((props) => {
  const { open, onClose, itemSku } = props;
  const [selectedSupplier, setSelectedSupplier] = useState<number | undefined>();
  const [selectedDateRange, setSelectedDateRange] = useState<string>(DATE_RANGE_TYPES.last_6_month);
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);

  const { data: historicalData = [] } = usePurchaseOrderItemHistoricalDataQuery(
    { sku: itemSku, supplierId: selectedSupplier, dateRange: selectedDateRange },
    { skip: !open || !itemSku }
  );

  const chartData = useMemo(() => {
    return historicalData?.map((i: { price: null | number; [key: string]: any }) => {
      return {
        ...i,
        price: i.price || 0,
      };
    });
  }, [historicalData]);

  const processedChartData = useProcessedChartData(chartData || [], selectedDateRange);
  const chartConfig = useChartConfiguration(selectedDateRange, processedChartData);
  const { handleDotClick } = useChartNavigation();

  // Generate unique key for each data item for hover synchronization
  const getItemKey = useCallback((item: any) => `${item.date}-${item.price}`, []);

  // Optimized hover handlers to prevent unnecessary rerenders
  const handleTableRowHover = useCallback((key: string | null) => {
    setHoveredItemKey(key);
  }, []);

  const handleItemClick = useCallback(
    (item: any) => {
      handleDotClick(item);
    },
    [handleDotClick]
  );

  const tooltipFormatter = (value: any, name: string, props: any) => {
    const data = props.payload;
    return [
      <div key="tooltip-content">
        <div style={{ marginBottom: '4px' }}>
          <strong style={{ color: data.price === 0 ? '#999' : '#05796B' }}>€{value}</strong>
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Date: {data.fullDate}</div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
          Source: {screamingSnakeToTitleCase(data.source) || 'No Data'}
        </div>
        {data.price > 0 && data.source !== 'No Data' && (
          <div style={{ fontSize: '11px', color: '#999', marginTop: '8px', fontStyle: 'italic' }}>
            <Badge color="#05796B" text="" /> Click on the dot to navigate to source
          </div>
        )}
      </div>,
    ];
  };

  useEffect(() => {
    if (!open) {
      setSelectedSupplier(undefined);
      setSelectedDateRange(DATE_RANGE_TYPES.last_6_month);
      setHoveredItemKey(null);
    }
  }, [open]);

  return (
    <FMGModal
      open={open}
      onCancel={onClose}
      title="Item Historical Data"
      width={950}
      cancelText="OK"
      onOk={onClose}
      footer={[
        <Button key={'ok'} size="large" onClick={onClose} type="primary" ghost>
          OK
        </Button>,
      ]}
    >
      <Row gutter={[0, 24]}>
        {/* Header Controls */}
        <Col span={24}>
          <Row gutter={16} align="middle">
            <Col span={12}>
              <Text strong className="font-size-16">
                Supplier
              </Text>
              <br />
              <SupplierSelect
                allowClear
                style={{ width: '100%', marginTop: '8px' }}
                placeholder="Select supplier"
                onClear={() => setSelectedSupplier(undefined)}
                value={selectedSupplier}
                onChange={(value) => setSelectedSupplier(value)}
              />
            </Col>
            <Col span={12}>
              <Text strong className="font-size-16">
                Date Range
              </Text>
              <br />
              <Select
                style={{ width: '100%', marginTop: '8px' }}
                placeholder="Select date range"
                value={selectedDateRange}
                onChange={(value) => setSelectedDateRange(value)}
              >
                {DATE_RANGE_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Col>

        {/* Chart Section */}
        <Col span={24}>
          <HistoricalChart
            data={processedChartData.data || []}
            chartConfig={chartConfig}
            tooltipFormatter={tooltipFormatter}
            hoveredItemKey={hoveredItemKey}
            onDotClick={handleItemClick}
            getItemKey={getItemKey}
          />
        </Col>

        {/* Data Table Section */}
        {chartData && chartData.length > 0 && (
          <Col span={24}>
            <HistoricalDataTable
              data={processedChartData.data || []}
              hoveredItemKey={hoveredItemKey}
              onRowHover={handleTableRowHover}
              onItemClick={handleItemClick}
              getItemKey={getItemKey}
            />
          </Col>
        )}
      </Row>
    </FMGModal>
  );
});
